import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Plus, Trash2, Feather, MessageCircle } from 'lucide-react';
import { useChatList, useChatDetail, useDeleteChat } from '../hooks/useChatHistory';

// --- Date helpers --------------------------------------------------------

const MS_DAY = 24 * 60 * 60 * 1000;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function relativeStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24 && d.getDate() === now.getDate()) return `${diffHr}h ago`;
  const startToday = startOfDay(now).getTime();
  const startOfThat = startOfDay(d).getTime();
  const daysAgo = Math.round((startToday - startOfThat) / MS_DAY);
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function groupFor(iso) {
  if (!iso) return 'Earlier';
  const d = startOfDay(new Date(iso)).getTime();
  const today = startOfDay(new Date()).getTime();
  const daysAgo = Math.round((today - d) / MS_DAY);
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7) return 'This week';
  if (daysAgo < 30) return 'This month';
  return 'Earlier';
}

const GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Earlier'];

// --- Sub-components ------------------------------------------------------

function RowSkeleton() {
  return (
    <div className="bg-stone-50/70 rounded-2xl p-5 border border-stone-100/80 animate-pulse">
      <div className="h-4 w-2/3 bg-stone-200/70 rounded-full mb-3" />
      <div className="h-3 w-full bg-stone-200/50 rounded-full mb-2" />
      <div className="h-3 w-1/3 bg-stone-200/40 rounded-full" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-16 px-6">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-stone-50 border border-stone-100 mb-5">
        <Feather size={20} className="text-stone-400" />
      </div>
      <p className="text-stone-600 font-serif text-base leading-relaxed max-w-[16rem] mx-auto">
        Your past chats live here.
      </p>
      <p className="text-stone-400 text-xs font-sans mt-2 max-w-[18rem] mx-auto leading-relaxed">
        Start a conversation and it will appear after the first reply.
      </p>
    </div>
  );
}

function ConversationRow({ conv, onOpen, onAskDelete }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(conv.id)}
      className="group w-full text-left bg-white hover:bg-stone-50/80 transition-colors duration-300 ease-out rounded-2xl p-5 border border-stone-100/80 hover:border-stone-200 hover:-translate-y-0.5 transform transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C9A82]/40"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-[15px] text-stone-800 leading-snug truncate">
            {conv.title || 'Untitled chat'}
          </h3>
          {conv.preview && (
            <p className="mt-1.5 text-xs text-stone-500 font-sans italic leading-relaxed line-clamp-1">
              {conv.preview}
            </p>
          )}
          <p className="mt-2 text-[10px] text-stone-400 font-sans uppercase tracking-widest">
            {relativeStamp(conv.last_message_at)}
            <span className="mx-1.5 text-stone-300">·</span>
            {conv.message_count} message{conv.message_count === 1 ? '' : 's'}
          </p>
        </div>
        <span
          role="button"
          tabIndex={0}
          aria-label="Delete chat"
          onClick={(e) => {
            e.stopPropagation();
            onAskDelete(conv);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onAskDelete(conv);
            }
          }}
          className="shrink-0 p-2 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-200"
        >
          <Trash2 size={15} />
        </span>
      </div>
    </button>
  );
}

function DeleteConfirm({ conv, onCancel, onConfirm, isPending }) {
  if (!conv) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm delete chat"
    >
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-[1.75rem] shadow-2xl w-full max-w-sm p-7 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300">
        <div>
          <h3 className="font-serif text-lg text-stone-800 leading-snug">Delete this chat?</h3>
          <p className="mt-2 text-sm text-stone-500 font-sans leading-relaxed">
            "{(conv.title || 'Untitled chat').slice(0, 60)}" will be removed from your history.
            You can't undo this.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 rounded-full text-sm font-sans text-stone-600 hover:bg-stone-50 transition-colors duration-300"
          >
            Keep it
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="px-5 py-2.5 rounded-full text-sm font-sans text-white bg-stone-800 hover:bg-stone-900 transition-colors duration-300 disabled:opacity-50"
          >
            {isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main ----------------------------------------------------------------

export default function ChatHistoryDrawer({ open, onClose, userId, onPickChat, onNewChat }) {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [loadingCid, setLoadingCid] = useState(null);
  const closeBtnRef = useRef(null);

  const list = useChatList(userId);
  const del = useDeleteChat(userId);
  const detailQuery = useChatDetail(userId, loadingCid);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    closeBtnRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // When detail finishes loading after row tap, hand it up + close drawer
  useEffect(() => {
    if (!loadingCid) return;
    if (detailQuery.data) {
      onPickChat(detailQuery.data);
      setLoadingCid(null);
      onClose();
    }
    if (detailQuery.error) {
      setLoadingCid(null);
    }
  }, [loadingCid, detailQuery.data, detailQuery.error, onPickChat, onClose]);

  const allConvs = useMemo(() => {
    const pages = list.data?.pages || [];
    return pages.flatMap((p) => p.conversations || []);
  }, [list.data]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of allConvs) {
      const g = groupFor(c.last_message_at);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(c);
    }
    return GROUP_ORDER
      .filter((g) => map.has(g))
      .map((g) => ({ group: g, items: map.get(g) }));
  }, [allConvs]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Chat history"
    >
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose} />

      <aside className="relative h-full w-full sm:max-w-sm bg-[#FDFCF8] shadow-2xl flex flex-col animate-in slide-in-from-right-4 duration-300 ease-out sm:rounded-l-[2rem] overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-stone-100/70 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-stone-800 text-white flex items-center justify-center shadow-sm">
              <MessageCircle size={15} />
            </div>
            <div>
              <h2 className="font-serif text-lg text-stone-800 leading-none">Chats</h2>
              <p className="text-[10px] text-stone-400 font-sans uppercase tracking-widest mt-1">
                Pick up where you left off
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { onNewChat(); onClose(); }}
              aria-label="Start a new chat"
              title="New chat"
              className="p-2.5 rounded-full text-[#7C9A82] hover:bg-[#7C9A82]/10 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C9A82]/40"
            >
              <Plus size={18} />
            </button>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label="Close chat history"
              className="p-2.5 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-200"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-6 space-y-8">
          {list.isLoading && (
            <div className="space-y-3">
              <RowSkeleton />
              <RowSkeleton />
              <RowSkeleton />
            </div>
          )}

          {!list.isLoading && allConvs.length === 0 && !list.isError && <EmptyState />}

          {list.isError && (
            <p className="text-center text-sm text-stone-500 font-sans py-12">
              We couldn't load your chats. Try again in a moment.
            </p>
          )}

          {grouped.map(({ group, items }) => (
            <section key={group} className="space-y-3">
              <h4 className="text-[10px] font-sans font-bold uppercase tracking-widest text-stone-400 px-1">
                {group}
              </h4>
              <div className="space-y-3">
                {items.map((conv) => (
                  <ConversationRow
                    key={conv.id}
                    conv={conv}
                    onOpen={(cid) => setLoadingCid(cid)}
                    onAskDelete={(c) => setPendingDelete(c)}
                  />
                ))}
              </div>
            </section>
          ))}

          {list.hasNextPage && (
            <div className="pt-2 text-center">
              <button
                type="button"
                onClick={() => list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                className="text-xs font-sans uppercase tracking-widest text-[#7C9A82] hover:text-stone-700 transition-colors duration-300 disabled:opacity-50"
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Show earlier'}
              </button>
            </div>
          )}

          {loadingCid && (
            <p className="text-center text-xs text-stone-400 font-sans uppercase tracking-widest py-2">
              Opening…
            </p>
          )}
        </div>
      </aside>

      <DeleteConfirm
        conv={pendingDelete}
        isPending={del.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const cid = pendingDelete.id;
          setPendingDelete(null);
          del.mutate(cid);
        }}
      />
    </div>
  );
}
